import { useEffect, useRef, useState } from "react";
import type { SupportReportContext } from "@compliance-sage/contracts";
import { ApiError, api } from "../../api/client";
import { useI18n } from "../../i18n";
import { Row, Section } from "./diagnosticsFields";

/**
 * The context the report is about to carry, with its real values. Showing it is
 * what earns the shorter form: a reporter can see that the run, the frozen
 * source set, and the rule version are already going with the report, so there
 * is nothing left for them to describe. Read through the same collector that
 * stores the bundle, so the preview cannot drift from the ticket.
 */
export function AttachedContextPanel({
  reviewId,
  findingId,
  checklistReferenceId,
  onUnavailable,
}: {
  reviewId: string;
  findingId?: string;
  checklistReferenceId?: string;
  /** Called when the server will not resolve this anchor for this reporter. */
  onUnavailable?: () => void;
}) {
  const { t, te, formatDateTime } = useI18n();
  const [context, setContext] = useState<SupportReportContext | null>(null);
  const [failed, setFailed] = useState(false);
  // Held in a ref so a caller passing an inline arrow does not re-trigger the
  // fetch on every keystroke in the form around this panel.
  const unavailable = useRef(onUnavailable);
  unavailable.current = onUnavailable;

  useEffect(() => {
    let live = true;
    api
      .supportReportContext({
        reviewId,
        findingId,
        checklistReferenceId,
        pagePath: window.location.pathname,
      })
      .then((value) => {
        if (live) setContext(value);
      })
      .catch((error: unknown) => {
        // A failed preview must not block the report: the server collects the
        // same bundle again on submit regardless of what was shown here.
        if (!live) return;
        setFailed(true);
        // Only a refusal means the anchor itself is wrong. A timeout or an
        // offline browser must not strip context off a report the reporter
        // raised from the right screen — the submit will resolve it again.
        const status = error instanceof ApiError ? error.status : null;
        if (status === 403 || status === 404) unavailable.current?.();
      });
    return () => {
      live = false;
    };
  }, [reviewId, findingId, checklistReferenceId]);

  if (failed) return null;

  return (
    <details className="rounded-md border border-slate-200 bg-slate-50 p-3">
      <summary className="cursor-pointer text-xs font-semibold text-slate-600">
        {t("supportAttachedContextAt")}
      </summary>
      {context === null ? (
        <p className="mt-2 text-xs text-slate-500">{t("supportAttachedContextLoading")}</p>
      ) : (
        <div className="mt-3 space-y-4">
          <Section title={t("diagReview")}>
            <Row label={t("diagReview")} value={context.diagnostics.reviewTitle} />
            <Row
              label={t("diagReviewStatus")}
              value={
                context.diagnostics.reviewStatus
                  ? te("reviewStatus", context.diagnostics.reviewStatus)
                  : null
              }
            />
            <Row
              label={t("diagRisk")}
              value={
                context.diagnostics.riskStatus
                  ? te("riskStatus", context.diagnostics.riskStatus)
                  : null
              }
            />
            <Row label={t("diagRevision")} value={num(context.diagnostics.revisionNumber)} />
          </Section>

          <Section title={t("diagAnalysisRun")}>
            <Row label={t("diagAnalysisRun")} value={context.diagnostics.analysisRunId} />
            <Row label={t("diagFingerprint")} value={context.diagnostics.sourceSetFingerprint} />
            <Row label={t("diagSourceCount")} value={num(context.diagnostics.sourceCount)} />
            <Row
              label={t("diagProgressStage")}
              value={context.diagnostics.progressStage}
            />
            <Row label={t("diagLastError")} value={context.diagnostics.jobLastError} />
          </Section>

          <Section title={t("diagChecklistItem")}>
            <Row
              label={t("diagChecklistItem")}
              value={context.diagnostics.checklistTitle ?? context.diagnostics.checklistReferenceId}
            />
            <Row
              label={t("diagChecklistVerdict")}
              value={
                context.diagnostics.checklistVerdict
                  ? te("checkVerdict", context.diagnostics.checklistVerdict)
                  : null
              }
            />
          </Section>

          <Section title={t("diagFinding")}>
            <Row label={t("diagFinding")} value={context.diagnostics.findingTitle} />
            <Row label={t("diagCitation")} value={context.diagnostics.findingCitationTitle} />
            <Row label={t("diagRuleVersion")} value={context.diagnostics.findingRuleVersionId} />
            <Row label={t("diagPolicyVersion")} value={context.diagnostics.findingPolicyVersionId} />
          </Section>

          <Section title={t("diagCollectedAt")}>
            <Row
              label={t("diagCollectedAt")}
              value={formatDateTime(context.diagnostics.collectedAt)}
            />
          </Section>

          <p className="text-xs leading-relaxed text-slate-500">
            {t("supportAttachedContextHelp")}
          </p>
        </div>
      )}
    </details>
  );
}

function num(value: number | null): string | null {
  return value === null ? null : String(value);
}
